import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, DatabaseBackup, Search } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import type { BackupFile } from '@/services/modules/BackupsService'
import { DegreeRecoveryService, type DegreeRecoveryPreview } from '@/services/modules/DegreeRelationshipsService'

/** "amplamais-2026-10-21-060005.dump" → "21/10/2026 às 06:00". */
function backupLabel(b: BackupFile): string {
  const m = b.name.match(/(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})/)
  const quando = m ? `${m[3]}/${m[2]}/${m[1]} às ${m[4]}:${m[5]}` : b.name
  return `${quando} · ${(b.size / 1048576).toFixed(0)} MB`
}

const plural = (n: number, um: string, varios: string) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`

/**
 * Devolve o grau de parentesco a quem perdeu: grau excluído (no backup ou na
 * lixeira) → grau ativo que o substitui. Só preenche quem está sem grau hoje.
 */
export function DegreeRecoveryModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [backups, setBackups] = useState<BackupFile[]>([])
  const [suggestedBackup, setSuggestedBackup] = useState<string | null>(null)
  const [backup, setBackup] = useState('')
  const [preview, setPreview] = useState<DegreeRecoveryPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState<'backups' | 'preview' | 'apply' | null>('backups')

  // A página só monta o modal aberto: cada abertura começa do zero.
  useEffect(() => {
    DegreeRecoveryService.backups()
      .then((r) => { setBackups(r.backups); setSuggestedBackup(r.suggested); setBackup(r.suggested ?? '') })
      .catch((e) => toast.error(extractApiError(e).message))
      .finally(() => setLoading(null))
  }, [])

  async function check() {
    setLoading('preview')
    try {
      const p = await DegreeRecoveryService.preview(backup || null)
      setPreview(p)
      setMapping(Object.fromEntries(p.items.map((i) => [i.id, i.suggested ?? ''])))
    } catch (e) {
      toast.error(extractApiError(e).message)
    } finally {
      setLoading(null)
    }
  }

  const escolhidos = useMemo(() => (preview?.items ?? []).filter((i) => mapping[i.id]), [preview, mapping])
  const totalDependentes = escolhidos.reduce((n, i) => n + i.dependents.semGrau, 0)

  async function apply() {
    if (!preview) return
    setLoading('apply')
    try {
      const r = await DegreeRecoveryService.apply(preview.token, preview.items.map((i) => ({ oldId: i.id, targetId: mapping[i.id] || null })))
      toast.success({
        title: 'Vínculos recuperados',
        description: `${plural(r.dependents, 'dependente voltou', 'dependentes voltaram')} a ter grau; ${plural(r.rules, 'regra recolocada', 'regras recolocadas')} em planos, produtos e relatórios.`,
      })
      onDone()
      onClose()
    } catch (e) {
      toast.error(extractApiError(e).message)
    } finally {
      setLoading(null)
    }
  }

  const backupOptions = [
    ...backups.map((b) => ({ value: b.path, label: backupLabel(b) + (b.path === suggestedBackup ? ' (sugerido)' : '') })),
    { value: '', label: 'Sem backup: só os graus da lixeira' },
  ]

  return (
    <Modal open onClose={onClose} size="xl"
      title="Graus excluídos: recuperar vínculos"
      description="Quando um grau é excluído e cadastrado de novo, os dependentes antigos ficam sem grau. Escolha o backup de antes da exclusão, confira e indique o grau ativo que substitui cada um."
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={apply} loading={loading === 'apply'} disabled={!preview || escolhidos.length === 0 || !!loading}>
          {escolhidos.length === 0 ? 'Indique o grau que substitui' : `Devolver o grau a ${plural(totalDependentes, 'dependente', 'dependentes')}`}
        </Button>
      </>}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <Select wrapperClassName="min-w-[300px] flex-1" label="Backup de antes da exclusão" options={backupOptions} value={backup}
            clearable={false} onChange={(e) => { setBackup(e.target.value); setPreview(null) }}
            hint="O sugerido é o último backup antes do cadastro mais recente de grau." />
          <Button variant="secondary" iconLeft={<Search size={14} />} onClick={check} loading={loading === 'preview'} disabled={!!loading}>
            Conferir
          </Button>
        </div>

        {loading === 'preview' && (
          <p className="flex items-center gap-2 text-sm text-gray-600"><DatabaseBackup size={16} /> Lendo o backup. Pode levar alguns segundos.</p>
        )}

        {preview && preview.items.length === 0 && (
          <div className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
            Nenhum grau excluído com dependentes sem grau {preview.backup ? 'neste backup' : 'na lixeira'}.
            {preview.backup && ' Se a exclusão foi antes, escolha um backup mais antigo.'}
          </div>
        )}

        {preview && preview.items.length > 0 && (
          <ul className="divide-y divide-gray-100 rounded-lg ring-1 ring-gray-200">
            {preview.items.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-4">
                <div className="min-w-[260px] flex-1 space-y-1">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">{i.name}</span>
                    {i.code && <span className="text-xs text-gray-500">código {i.code}</span>}
                    <Badge variant={i.source === 'backup' ? 'amber' : 'gray'}>{i.source === 'backup' ? 'excluído (do backup)' : 'na lixeira'}</Badge>
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>{plural(i.dependents.semGrau, 'dependente', 'dependentes')}</strong> sem grau hoje
                    {' '}({plural(i.dependents.ativos, 'ativo', 'ativos')}, {plural(i.dependents.inativos, 'inativo', 'inativos')})
                  </p>
                  {i.dependents.jaCorrigidos > 0 && (
                    <p className="text-xs text-gray-500">{plural(i.dependents.jaCorrigidos, 'já foi corrigido', 'já foram corrigidos')} à mão e não muda.</p>
                  )}
                  {i.rules.length > 0 && (
                    <p className="text-xs text-gray-500">Também volta para: {i.rules.map((r) => `${r.count} ${r.label}`).join(', ')}.</p>
                  )}
                </div>
                <ArrowRight size={16} className="hidden shrink-0 text-gray-400 sm:block" />
                <Select wrapperClassName="w-64" label="Substituir por" value={mapping[i.id] ?? ''}
                  options={[{ value: '', label: 'Não recuperar' }, ...preview.targets.map((t) => ({ value: t.id, label: t.name }))]}
                  onChange={(e) => setMapping((m) => ({ ...m, [i.id]: e.target.value }))} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
